import { Flex, Group, Text } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  deleteHrUserById,
  getHrUsersList,
  getRoleList,
} from "../../api/hrUserApi";
import { useAuth } from "../../context/AuthContext";
import { notifications } from "@mantine/notifications";
import TableDeleteButton from "../common/Buttons/TableDeleteButton";
import TableEditButton from "../common/Buttons/TableEditButton";
import styled, { keyframes, css } from "styled-components";
import CustomSelect from "../common/Inputs/CustomSelect";
import DataTable, { Column } from "../common/Table/DataTable";
import { useTableQuery } from "../../hooks/useTableQuery";
import { useDeleteWithConfirm } from "../../hooks/useDeleteWithConfirm";

const PanelUsersTable = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectData, setSelectData] = useState<any[]>([]);

  const table = useTableQuery({ fetcher: getHrUsersList });

  useEffect(() => {
    const getRoles = async () => {
      try {
        const { data }: any = await getRoleList();
        setSelectData(data || []);
      } catch (error) {
        console.error(error);
      }
    };
    getRoles();
  }, []);

  // Kullanıcı kendi hesabını silemez; bu kontrol silme isteği atılmadan önce yapılır.
  const deleteFn = useCallback(
    async ({ id }: { id: string }) => {
      if (user?._id === id) {
        notifications.show({
          title: "Hata",
          message: "Kendi hesabınızı silemezsiniz.",
          color: "red",
          autoClose: 4000,
          withBorder: true,
        });
        return { data: { success: false, message: "Kendi hesabınızı silemezsiniz." } };
      }
      return deleteHrUserById({ id });
    },
    [user]
  );

  const { askDelete, modal } = useDeleteWithConfirm({
    deleteFn,
    onDeleted: table.refetch,
    title: "Panel Kullanıcısı Sil",
    text: "Bu kullanıcıyı silmek istediğinizden emin misiniz?",
  });

  const columns: Column[] = [
    {
      key: "name",
      header: `Ad Soyad ${table.sort === "asc" ? "↑" : "↓"}`,
      width: "30%",
      onHeaderClick: () => table.setSort(table.sort === "asc" ? "desc" : "asc"),
      render: (row) => (
        <Group gap="sm" wrap="nowrap">
          <SimpleAvatar>
            {row.name ? row.name[0].toUpperCase() : "?"}
          </SimpleAvatar>
          <Text fw={400} size="md" style={{ color: "#111827" }}>
            {row.name}
          </Text>
        </Group>
      ),
    },
    {
      key: "phone",
      header: "Telefon",
      width: "25%",
      render: (row) => (
        <Text size="md" fw={400} style={{ color: "#111827" }}>
          {row.phone || "—"}
        </Text>
      ),
    },
    {
      key: "privilege",
      header: "Yetki / Rol",
      width: "25%",
      render: (row) => {
        const isAdmin = row.privilege?.toLowerCase() === "admin";
        return (
          <StatusPill $isAdmin={isAdmin}>
            <StatusDot $isAdmin={isAdmin} />
            <span>{row.privilege || "—"}</span>
          </StatusPill>
        );
      },
    },
    {
      key: "actions",
      header: "İşlemler",
      width: "20%",
      headerAlign: "right",
      render: (row) => (
        <Flex justify="flex-end" gap={6} onClick={(e) => e.stopPropagation()}>
          <TableEditButton
            onClick={() =>
              navigate(`/panel-users/${row._id}/edit`, {
                state: { panelUser: row },
              })
            }
          />
          <TableDeleteButton onClick={() => askDelete(row)} />
        </Flex>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={table.rows}
        loading={table.loading}
        emptyText="Panel kullanıcısı bulunamadı."
        searchValue={table.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Kullanıcı ara..."
        toolbar={
          <CustomSelect
            placeholder="Yetki/Rol"
            data={selectData}
            clearable
            onChange={(value: any) =>
              table.setFilter("privilege", value ?? undefined)
            }
          />
        }
        page={table.page}
        totalPages={table.totalPages}
        onPageChange={table.setPage}
        limit={table.limit}
        onLimitChange={table.setLimit}
      />
      {modal}
    </>
  );
};

export default PanelUsersTable;

const pulseDot = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
  60% { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
`;

const SimpleAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e1e7fc;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 14px;
  border: 1px solid #d1d9f5;
  flex-shrink: 0;
`;

const StatusPill = styled.div<{ $isAdmin: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  min-width: 85px;
  justify-content: center;

  ${(p) =>
    p.$isAdmin
      ? css`
          color: #059669;
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
        `
      : css`
          color: #d97706;
          background: #fffbeb;
          border: 1px solid #fef3c7;
        `}
`;

const StatusDot = styled.div<{ $isAdmin: boolean }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => (p.$isAdmin ? "#10B981" : "#f59e0b")};
  ${(p) =>
    p.$isAdmin &&
    css`
      animation: ${pulseDot} 2s infinite;
    `}
`;
